import _, {first} from 'lodash';

import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ChannelModel} from '../../../libs/shared/core/src/models/api/channelModel';
import {DraftMessageModel} from '../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {SignatureModel} from '../../models/api/signatureModel';
import {AppState} from '../../store/appState';
import {DataState} from '../../store/data/dataState';
import {makeGetVariableInterpolator} from '../../store/data/messages/messagesSelectors';
import {doesChannelSupportSignatures} from './channelHelpers';

/** Find the signatures that can be used for a specific channel. */
export function selectSignaturesForChannel(
  data: DataState,
  channel: ChannelModel,
): ReadonlyArray<SignatureModel> {
  const user = data.teammates[data.userId];
  if (!user) {
    return [];
  }

  const userSignatures = selectSignaturesForNamespace(data, user.namespace);

  return userSignatures.filter((signature) =>
    isSignatureAllowedForChannel(user, channel, channel.namespace, signature),
  );
}

export function selectSortedSignaturesByNamespace(
  data: DataState,
  namespace: string,
): ReadonlyArray<SignatureModel> {
  return _(data.signatures.byId)
    .values()
    .compact()
    .filter((signature) => signature.namespace === namespace)
    .sortBy((signature) => signature.sortOrder)
    .value();
}

export function selectSignaturesForNamespace(
  data: DataState,
  namespace: string,
): ReadonlyArray<SignatureModel> {
  return _(data.signatures.byId)
    .values()
    .compact()
    .sortBy(
      (signature) => signature.namespace !== namespace,
      (signature) => signature.sortOrder,
    )
    .value();
}

export function mapToSignatureIds(signatures: ReadonlyArray<SignatureModel>): ReadonlyArray<number> {
  return _(signatures)
    .map((signature) => signature.id)
    .value();
}

export function selectSortedSignaturesInNamespace(
  signatures: ReadonlyArray<SignatureModel>,
  namespace: string,
): ReadonlyArray<SignatureModel> {
  return _(signatures)
    .filter((signature) => signature.namespace === namespace)
    .sortBy((signature) => signature.sortOrder)
    .compact()
    .value();
}

/**
 * Check whether a given signature is allowed for the specified channel.
 *
 *  See documentation in `RSignatureTemplateDirty` for {@see SignatureModel} for model property details
 *  as the meanings of the allChannels and allPrivateChannels properties change for a shared vs. private signature.
 */
function isSignatureAllowedForChannel(
  user: TeammateModel,
  channel: ChannelModel,
  channelNamespace: string,
  signature: SignatureModel,
) {
  const {namespace: userNamespace} = user;
  // TODO: add custom right check here.
  if (!doesChannelSupportSignatures(channel)) {
    return false;
  }

  // Private signature.
  if (signature.namespace === userNamespace) {
    return signature.allChannels || signature.channelIds.includes(channel.id);
  }

  // Shared signature, private channel.
  if (channelNamespace === userNamespace) {
    // If user does not have access to the namespace of the signature, do not show the it.
    return signature.allPrivateChannels && isDefined(user.roleByNamespace[signature.namespace]);
  }

  // Shared signature, shared channel.
  return (
    signature.namespace === channelNamespace &&
    (signature.allChannels || signature.channelIds.includes(channel.id))
  );
}

export function findSignature(
  signatureId: number | undefined,
  availableSignatures: ReadonlyArray<SignatureModel>,
): SignatureModel | undefined {
  const signature = signatureId && availableSignatures.find((s) => s.id === signatureId);
  return signature || first(availableSignatures);
}

/** Build the signature HTML for the specified message, if necessary. */
export function buildSignatureHtml(state: AppState, message: DraftMessageModel) {
  // Only build if the message doesn't already have it.
  if (message.signatureHtml) {
    return message.signatureHtml;
  }

  // Find the signature for this message.

  const signature = message.signatureId && state.data.signatures.byId[message.signatureId];
  if (!signature) {
    return undefined;
  }

  // If one was found, build the interpolator and build the final HTML.
  const interpolator = makeGetVariableInterpolator(() => message)(state, {});
  return interpolator.interpolateHtml(signature.body);
}
